import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import { useCurrency } from "./CurrencyContext";

const API = process.env.REACT_APP_API_URL || "";
const PENDING_PAYMENT_KEY = "sm_pending_payment_id";

const paymentEndpointForCurrency = (currency) =>
  currency === "RUB" ? "/api/payments/create" : "/api/payments/trybit/create";

const syncEndpointForProvider = (provider) =>
  provider === "trybit" ? "/api/payments/trybit/sync" : "/api/payments/sync";

const readPendingPayment = () => {
  const raw = localStorage.getItem(PENDING_PAYMENT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.payment_id) return parsed;
  } catch {
    // Legacy storage held a bare YooKassa payment id.
  }
  return { provider: "yookassa", payment_id: raw };
};

const writePendingPayment = (provider, paymentId) => {
  localStorage.setItem(
    PENDING_PAYMENT_KEY,
    JSON.stringify({ provider, payment_id: String(paymentId) })
  );
};

const Subscription = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const { currency, setCurrency, getPrice, currencies } = useCurrency();
  const navigate = useNavigate();

  const [billing, setBilling] = useState("monthly");
  const [selectedTier, setSelectedTier] = useState("premium");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [hasSub, setHasSub] = useState(false);
  const [credits, setCredits] = useState(null);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyRef = useRef(null);

  useEffect(() => {
    if (!user?.email || !token) return;
    let cancelled = false;
    let retryTimer;

    const refreshSubscription = async () => {
      const response = await fetch(`${API}/api/user/tokens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not load subscription");
      if (!cancelled) {
        setCredits(data.credits ?? data.tokens ?? null);
        setHasSub(data.subscription_status === "active");
      }
      return data;
    };

    const syncPayment = async (pendingPayment, attempt = 0) => {
      const provider = pendingPayment.provider || "yookassa";
      const paymentId = pendingPayment.payment_id;
      try {
        const response = await fetch(`${API}${syncEndpointForProvider(provider)}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ payment_id: paymentId }),
        });
        const data = await response.json();

        if (response.status === 202) {
          if (attempt < 10) {
            retryTimer = setTimeout(() => syncPayment(pendingPayment, attempt + 1), 2000);
            return;
          }
          throw new Error(t("subscription.paymentSyncFailed"));
        }
        if (!response.ok) throw new Error(data.error || t("subscription.paymentSyncFailed"));

        localStorage.removeItem(PENDING_PAYMENT_KEY);
        await refreshSubscription();
        window.dispatchEvent(new Event("sm-subscription-updated"));
        if (!cancelled) setLoading(false);
      } catch (syncError) {
        if (!cancelled) {
          setError(syncError.message || t("subscription.paymentSyncFailed"));
          setLoading(false);
        }
      }
    };

    refreshSubscription().catch(() => {});
    const pendingPayment = readPendingPayment();
    if (pendingPayment) {
      setLoading(true);
      syncPayment(pendingPayment);
    }

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [user, token, t]);

  useEffect(() => {
    const handler = (e) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target))
        setCurrencyOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedPlanId = `${selectedTier}_${billing}`;

  const handleSubscribe = async () => {
    if (!user) { navigate("/log-in"); return; }
    setLoading(true);
    setError("");
    try {
      const priceInfo = getPrice(selectedPlanId);
      const endpoint = paymentEndpointForCurrency(currency);
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan: selectedPlanId,
          user_id: user.id || user.user_id,
          email: user.email,
          return_url: `${window.location.origin}/premium`,
          currency,
          display_price: priceInfo.display,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Payment error");
      writePendingPayment(data.provider || (currency === "RUB" ? "yookassa" : "trybit"), data.payment_id);
      window.location.href = data.confirmation_url;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const allPlans = t("subscription.plans");
  const tierPlans = {
    pro:     { monthly: allPlans.find((p) => p.id === "pro_monthly"),     yearly: allPlans.find((p) => p.id === "pro_yearly")     },
    premium: { monthly: allPlans.find((p) => p.id === "premium_monthly"), yearly: allPlans.find((p) => p.id === "premium_yearly") },
  };

  const features       = t("subscription.features");
  const compareRows    = t("subscription.compareRows");
  const compareHeaders = t("subscription.compareHeaders");
  const faqs           = t("subscription.faqs");
  const activePlan     = tierPlans[selectedTier]?.[billing];
  const priceInfo      = getPrice(selectedPlanId);

  return (
    <PageContainer>
      <Header />

      {/* Hero */}
      <HeroSection>
        <Blobs>
          <Blob style={{ top: "10%", left: "5%", width: 420, height: 420, animationDelay: "0s" }} />
          <Blob style={{ top: "30%", right: "8%", width: 300, height: 300, animationDelay: "1.8s" }} />
        </Blobs>
        <HeroOverlay />
        <HeroContent
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <HeroBadge>{t("subscription.heroBadge")}</HeroBadge>
          <HeroTitle>
            {t("subscription.heroTitle")}<br />
            <span style={{ color: "#4CAF50" }}>{t("subscription.heroTitleAccent")}</span>
          </HeroTitle>
          <HeroSub>{t("subscription.heroSub")}</HeroSub>
          {hasSub && <ActiveBadge>{t("subscription.activeBadge")}</ActiveBadge>}
          {!hasSub && credits !== null && (
            <CreditsLeft>
              {t("subscription.tokensLeft").replace("{{count}}", credits)}
            </CreditsLeft>
          )}
        </HeroContent>
      </HeroSection>

      {/* Plans */}
      <Section>
        <SectionInner>
          <PlansHeader>
            <div>
              <SectionEyebrow>{t("subscription.plansEyebrow")}</SectionEyebrow>
              <SectionTitle style={{ marginBottom: 0 }}>{t("subscription.plansTitle")}</SectionTitle>
            </div>

            {/* Currency picker */}
            <CurrencyPickerWrap ref={currencyRef}>
              <CurrencyBtn onClick={() => setCurrencyOpen((o) => !o)}>
                {currency}
                <CurrencyCaret open={currencyOpen}>▾</CurrencyCaret>
              </CurrencyBtn>
              <AnimatePresence>
                {currencyOpen && (
                  <CurrencyDropdown
                    as={motion.div}
                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                  >
                    {currencies.map((c) => (
                      <CurrencyOption
                        key={c.code}
                        active={c.code === currency}
                        onClick={() => { setCurrency(c.code); setCurrencyOpen(false); }}
                      >
                        <span style={{ fontWeight: 700, minWidth: 36 }}>{c.code}</span>
                        <span style={{ opacity: 0.5, fontSize: "0.75rem" }}>
                          {c.label.split(" — ")[1] || c.label}
                        </span>
                      </CurrencyOption>
                    ))}
                  </CurrencyDropdown>
                )}
              </AnimatePresence>
            </CurrencyPickerWrap>
          </PlansHeader>

          {/* Billing toggle */}
          <BillingToggle>
            <BillingBtn active={billing === "monthly"} onClick={() => setBilling("monthly")}>
              {t("subscription.billingMonthly")}
            </BillingBtn>
            <BillingBtn active={billing === "yearly"} onClick={() => setBilling("yearly")}>
              {t("subscription.billingYearly")}
              <SavePill>{t("subscription.billingSaveLabel")}</SavePill>
            </BillingBtn>
          </BillingToggle>

          {/* Plan cards */}
          <PlansRow>
            {["pro", "premium"].map((tier, i) => {
              const plan = tierPlans[tier]?.[billing];
              if (!plan) return null;
              const price = getPrice(plan.id);
              const isSelected = selectedTier === tier;
              return (
                <PlanCard
                  key={tier}
                  as={motion.div}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.05 + i * 0.1 }}
                  active={isSelected ? 1 : 0}
                  onClick={() => setSelectedTier(tier)}
                >
                  {plan.note && <PlanBadge>{plan.note}</PlanBadge>}
                  <PlanLabel>{plan.label}</PlanLabel>
                  <PlanCredits>
                    {plan.credits.toLocaleString()}{" "}
                    {billing === "yearly"
                      ? t("subscription.creditsPerYear")
                      : t("subscription.creditsPerMonth")}
                  </PlanCredits>
                  <PlanPriceRow>
                    <PlanPrice>{price.amount}</PlanPrice>
                    <PlanSymbol>{price.symbol}</PlanSymbol>
                  </PlanPriceRow>
                  <PlanPer>{plan.per}</PlanPer>
                  <PlanNote>{plan.perNote}</PlanNote>
                  <PlanSelect active={isSelected ? 1 : 0}>
                    {isSelected
                      ? t("subscription.planSelectedLabel")
                      : t("subscription.planSelectLabel")}
                  </PlanSelect>
                </PlanCard>
              );
            })}
          </PlansRow>

          <AnimatePresence>
            {error && (
              <ErrorBox
                as={motion.div}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                {error}
              </ErrorBox>
            )}
          </AnimatePresence>

          <PayBtnWrap>
            <PayBtn onClick={handleSubscribe} disabled={loading || hasSub}>
              {hasSub
                ? t("subscription.payBtnActive")
                : loading
                ? t("subscription.payBtnLoading")
                : `${activePlan?.label || ""} — ${priceInfo.display} ${activePlan?.per || ""} →`}
            </PayBtn>
            <PayLegal>{t("subscription.payLegal")}</PayLegal>
          </PayBtnWrap>
        </SectionInner>
      </Section>

      {/* Features */}
      <SectionGreen>
        <SectionInner>
          <SectionEyebrow light>{t("subscription.featuresEyebrow")}</SectionEyebrow>
          <SectionTitle light>{t("subscription.featuresTitle")}</SectionTitle>
          <FeaturesGrid>
            {features.map((f, i) => (
              <FeatureCard
                key={f.title}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
              >
                <FeatureIcon>{f.icon}</FeatureIcon>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </SectionInner>
      </SectionGreen>

      {/* Comparison table — 4 columns */}
      <Section>
        <SectionInner>
          <SectionEyebrow>{t("subscription.compareEyebrow")}</SectionEyebrow>
          <SectionTitle>{t("subscription.compareTitle")}</SectionTitle>
          <CompareTable>
            <CompareHead>
              <CompareCell header />
              <CompareCell header center>{compareHeaders[1]}</CompareCell>
              <CompareCell header center>{compareHeaders[2]}</CompareCell>
              <CompareCell header center accent>{compareHeaders[3]}</CompareCell>
            </CompareHead>
            {compareRows.map((row) => (
              <CompareRow key={row[0]}>
                <CompareCell>{row[0]}</CompareCell>
                <CompareCell center muted>{row[1]}</CompareCell>
                <CompareCell center green>{row[2]}</CompareCell>
                <CompareCell center green bold>{row[3]}</CompareCell>
              </CompareRow>
            ))}
          </CompareTable>
        </SectionInner>
      </Section>

      {/* FAQ */}
      <SectionGreen>
        <SectionInner narrow>
          <SectionEyebrow light>{t("subscription.faqEyebrow")}</SectionEyebrow>
          <SectionTitle light>{t("subscription.faqTitle")}</SectionTitle>
          <FaqList>
            {faqs.map((item, i) => (
              <FaqItem key={i}>
                <FaqQuestion onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{item.q}</span>
                  <FaqChevron open={openFaq === i}>›</FaqChevron>
                </FaqQuestion>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28 }}
                      style={{ overflow: "hidden" }}
                    >
                      <FaqAnswer>{item.a}</FaqAnswer>
                    </motion.div>
                  )}
                </AnimatePresence>
              </FaqItem>
            ))}
          </FaqList>
        </SectionInner>
      </SectionGreen>

      {/* Bottom CTA */}
      <CtaSection>
        <SectionInner>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: "center" }}
          >
            <CtaTitle>{t("subscription.ctaTitle")}</CtaTitle>
            <CtaSub>{t("subscription.ctaSub")}</CtaSub>
            <PayBtn
              onClick={handleSubscribe}
              disabled={loading || hasSub}
              style={{ maxWidth: 380, margin: "0 auto", display: "block" }}
            >
              {hasSub
                ? t("subscription.payBtnActive")
                : loading
                ? t("subscription.payBtnLoading")
                : t("subscription.ctaBtnStart")}
            </PayBtn>
          </motion.div>
        </SectionInner>
      </CtaSection>

      <Footer />
    </PageContainer>
  );
};

/* ── Keyframes ── */
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.18; }
  50%       { transform: scale(1.12); opacity: 0.28; }
`;

/* ── Styled ── */
const PageContainer = styled.div`
  display: flex; flex-direction: column; min-height: 100svh;
  background: #fff; color: #111; font-family: 'Montserrat', sans-serif;
`;

const HeroSection = styled.section`
  position: relative; overflow: hidden;
  background: #f5faf5; padding: 10rem 6vw 6rem;
  display: flex; align-items: center; justify-content: center;
  @media (max-width: 768px) { padding: 8rem 1.5rem 4rem; }
`;

const HeroOverlay = styled.div`
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 60% at 50% 0%, rgba(76,175,80,0.12) 0%, transparent 70%);
  pointer-events: none;
`;

const Blobs = styled.div`position: absolute; inset: 0; pointer-events: none; z-index: 0;`;

const Blob = styled.div`
  position: absolute; border-radius: 50%;
  background: radial-gradient(circle, rgba(76,175,80,0.22) 0%, transparent 70%);
  animation: ${pulse} 7s ease-in-out infinite; filter: blur(32px);
`;

const HeroContent = styled.div`position: relative; z-index: 1; text-align: center; max-width: 680px;`;

const HeroBadge = styled.div`
  display: inline-block; font-size: 0.68rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  background: rgba(76,175,80,0.12); border: 1px solid rgba(76,175,80,0.3);
  color: #4CAF50; padding: 0.35rem 1rem; border-radius: 20px; margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
  margin: 0 0 1rem; font-size: clamp(2.2rem, 5vw, 3.6rem);
  font-weight: 900; line-height: 1.15; letter-spacing: -0.02em; color: #111;
`;

const HeroSub = styled.p`
  margin: 0 0 2rem; font-size: clamp(0.9rem, 1.5vw, 1.05rem);
  color: rgba(0,0,0,0.55); line-height: 1.7;
`;

const ActiveBadge = styled.div`
  display: inline-block; background: rgba(76,175,80,0.15);
  border: 1px solid rgba(76,175,80,0.4); color: #4CAF50;
  font-size: 0.85rem; font-weight: 700; padding: 0.6rem 1.5rem; border-radius: 8px;
`;

const CreditsLeft = styled.div`font-size: 0.85rem; color: rgba(0,0,0,0.45);`;

const Section = styled.section`
  padding: 5rem 6vw; background: #fff;
  @media (max-width: 768px) { padding: 3.5rem 1.5rem; }
`;

const SectionGreen = styled.section`
  padding: 5rem 6vw; background: #f0f7f0;
  @media (max-width: 768px) { padding: 3.5rem 1.5rem; }
`;

const SectionInner = styled.div`
  max-width: ${({ narrow }) => narrow ? "640px" : "960px"}; margin: 0 auto;
`;

const SectionEyebrow = styled.div`
  font-size: 0.65rem; font-weight: 700; letter-spacing: 0.3em; text-transform: uppercase;
  color: ${({ light }) => light ? "rgba(76,175,80,0.8)" : "#4CAF50"}; margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 2.5rem; font-size: clamp(1.6rem, 3.5vw, 2.4rem);
  font-weight: 800; line-height: 1.15; color: #111;
`;

/* Plans header */
const PlansHeader = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap;
`;

/* Billing toggle */
const BillingToggle = styled.div`
  display: inline-flex; background: #f0f7f0;
  border: 1px solid rgba(76,175,80,0.2); border-radius: 8px;
  padding: 3px; margin-bottom: 1.75rem; gap: 2px;
`;

const BillingBtn = styled.button`
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.45rem 1.1rem;
  border: none; border-radius: 6px; font-family: inherit;
  font-size: 0.78rem; font-weight: 700; letter-spacing: 0.04em;
  cursor: pointer; transition: background 0.18s, color 0.18s;
  background: ${({ active }) => active ? "#4CAF50" : "transparent"};
  color: ${({ active }) => active ? "#000" : "rgba(0,0,0,0.45)"};
`;

const SavePill = styled.span`
  font-size: 0.6rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  background: rgba(0,0,0,0.12); border-radius: 10px;
  padding: 0.1rem 0.4rem; white-space: nowrap;
`;

/* Currency picker */
const CurrencyPickerWrap = styled.div`position: relative; flex-shrink: 0; align-self: center;`;

const CurrencyBtn = styled.button`
  display: flex; align-items: center; gap: 0.35rem;
  background: rgba(76,175,80,0.08); border: 1px solid rgba(76,175,80,0.25);
  border-radius: 6px; padding: 0.4rem 0.75rem;
  font-family: inherit; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.06em;
  color: #4CAF50; cursor: pointer; transition: background 0.15s;
  &:hover { background: rgba(76,175,80,0.14); }
`;

const CurrencyCaret = styled.span`
  font-size: 0.6rem; transition: transform 0.18s;
  transform: ${({ open }) => open ? "rotate(180deg)" : "none"};
`;

const CurrencyDropdown = styled.div`
  position: absolute; top: calc(100% + 6px); right: 0;
  min-width: 210px; max-height: 300px; overflow-y: auto;
  background: #fff; border: 1px solid rgba(0,0,0,0.08);
  border-radius: 10px; box-shadow: 0 8px 28px rgba(0,0,0,0.12); z-index: 200;
`;

const CurrencyOption = styled.div`
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.6rem 1rem; cursor: pointer;
  background: ${({ active }) => active ? "rgba(76,175,80,0.07)" : "transparent"};
  font-size: 0.82rem; transition: background 0.12s;
  &:hover { background: rgba(76,175,80,0.1); }
`;

/* Plan cards */
const PlansRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 1.25rem; margin-bottom: 2rem; max-width: 560px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const PlanCard = styled.div`
  position: relative;
  border: 2px solid ${({ active }) => active ? "#4CAF50" : "rgba(0,0,0,0.1)"};
  border-radius: 14px; padding: 2rem 1.5rem 1.5rem; cursor: pointer;
  background: ${({ active }) => active ? "rgba(76,175,80,0.06)" : "#f8faf8"};
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  box-shadow: ${({ active }) => active ? "0 4px 20px rgba(76,175,80,0.15)" : "0 1px 6px rgba(0,0,0,0.05)"};
  &:hover { border-color: ${({ active }) => active ? "#4CAF50" : "rgba(76,175,80,0.4)"}; }
`;

const PlanBadge = styled.div`
  position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
  background: #4CAF50; color: #000; font-size: 0.6rem; font-weight: 800;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 0.25rem 0.75rem; border-radius: 20px; white-space: nowrap;
`;

const PlanLabel = styled.div`
  font-size: 0.68rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
  color: rgba(0,0,0,0.4); margin-bottom: 0.4rem;
`;

const PlanCredits = styled.div`
  font-size: 0.72rem; color: #4CAF50; font-weight: 700; margin-bottom: 0.75rem;
`;

const PlanPriceRow = styled.div`display: flex; align-items: baseline; gap: 0.2rem;`;

const PlanPrice = styled.div`font-size: 2.4rem; font-weight: 900; line-height: 1; color: #111;`;
const PlanSymbol = styled.div`font-size: 1.1rem; font-weight: 700; color: rgba(0,0,0,0.55);`;
const PlanPer = styled.div`font-size: 0.75rem; color: rgba(0,0,0,0.35); margin-top: 0.2rem;`;
const PlanNote = styled.div`font-size: 0.72rem; color: #4CAF50; margin-top: 0.35rem; font-weight: 600; min-height: 1em;`;

const PlanSelect = styled.div`
  margin-top: 1.25rem; padding: 0.55rem; border-radius: 6px; text-align: center;
  font-size: 0.8rem; font-weight: 700; letter-spacing: 0.05em;
  background: ${({ active }) => active ? "#4CAF50" : "rgba(0,0,0,0.05)"};
  color: ${({ active }) => active ? "#fff" : "rgba(0,0,0,0.45)"};
  transition: background 0.2s, color 0.2s;
`;

const PayBtnWrap = styled.div`display: flex; flex-direction: column; align-items: flex-start; gap: 0.6rem; max-width: 560px;`;

const PayBtn = styled.button`
  width: 100%; padding: 1.1rem 1.5rem; background: #4CAF50; color: #000;
  font-family: inherit; font-size: 1rem; font-weight: 700;
  border: 2px solid #4CAF50; border-radius: 8px; cursor: pointer;
  letter-spacing: 0.02em; box-shadow: 0 0 28px rgba(76,175,80,0.3);
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  &:hover:not(:disabled) { background: transparent; color: #4CAF50; box-shadow: 0 0 40px rgba(76,175,80,0.5); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PayLegal = styled.p`font-size: 0.72rem; color: rgba(0,0,0,0.35); margin: 0;`;

const ErrorBox = styled.div`
  background: rgba(220,50,50,0.12); border: 1px solid rgba(220,50,50,0.35);
  border-radius: 6px; padding: 0.6rem 1rem; font-size: 0.82rem;
  color: #ff6b6b; margin-bottom: 1rem; max-width: 560px;
`;

const FeaturesGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem;
  @media (max-width: 720px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.div`
  background: #fff; border: 1px solid rgba(76,175,80,0.15);
  border-radius: 12px; padding: 1.5rem 1.25rem; box-shadow: 0 2px 10px rgba(0,0,0,0.05);
`;

const FeatureIcon = styled.div`font-size: 1.8rem; margin-bottom: 0.75rem;`;
const FeatureTitle = styled.div`font-size: 0.9rem; font-weight: 700; margin-bottom: 0.4rem; color: #111;`;
const FeatureDesc = styled.div`font-size: 0.8rem; color: rgba(0,0,0,0.5); line-height: 1.6;`;

/* Compare table — 4 columns */
const CompareTable = styled.div`border: 1px solid rgba(0,0,0,0.08); border-radius: 12px; overflow: hidden;`;

const CompareHead = styled.div`
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
  background: #f8f8f8; border-bottom: 1px solid rgba(0,0,0,0.08);
`;

const CompareRow = styled.div`
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(76,175,80,0.03); }
`;

const CompareCell = styled.div`
  padding: 0.85rem 1rem; font-size: 0.85rem;
  font-weight: ${({ header, bold }) => header || bold ? 700 : 400};
  text-align: ${({ center }) => center ? "center" : "left"};
  color: ${({ muted, green, accent }) =>
    muted ? "rgba(0,0,0,0.3)" : green ? "#4CAF50" : accent ? "#4CAF50" : "rgba(0,0,0,0.75)"};
  border-right: 1px solid rgba(0,0,0,0.05);
  &:last-child { border-right: none; }
`;

const FaqList = styled.div`display: flex; flex-direction: column; gap: 0.5rem;`;

const FaqItem = styled.div`
  border: 1px solid rgba(0,0,0,0.08); border-radius: 10px; overflow: hidden;
  background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const FaqQuestion = styled.button`
  display: flex; justify-content: space-between; align-items: center;
  width: 100%; padding: 1rem 1.25rem; background: none; border: none; color: #111;
  font-family: inherit; font-size: 0.9rem; font-weight: 600; text-align: left;
  cursor: pointer; gap: 1rem;
  &:hover { color: #4CAF50; }
`;

const FaqChevron = styled.span`
  font-size: 1.3rem; color: #4CAF50;
  transform: ${({ open }) => open ? "rotate(90deg)" : "rotate(0deg)"};
  transition: transform 0.25s; line-height: 1; flex-shrink: 0;
`;

const FaqAnswer = styled.div`padding: 0 1.25rem 1rem; font-size: 0.85rem; color: rgba(0,0,0,0.55); line-height: 1.65;`;

const CtaSection = styled.section`
  padding: 6rem 6vw; background: #fff;
  @media (max-width: 768px) { padding: 4rem 1.5rem; }
`;

const CtaTitle = styled.h2`margin: 0 0 1rem; font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 900;`;

const CtaSub = styled.p`
  margin: 0 0 2.5rem; font-size: clamp(0.88rem, 1.3vw, 1rem);
  color: rgba(0,0,0,0.45); max-width: 480px; margin-left: auto; margin-right: auto;
`;

export default Subscription;
